import {
  Allergen,
  SubscriptionItemType,
  SubscriptionItemVariantValue,
} from "@/primary-models";

export enum FreshRecipe {
  CLUCK = "CLUCK",
  GOBBLE = "GOBBLE",
  SHREDDED_CLUCK = "SHREDDED_CLUCK",
  SHREDDED_GOBBLE = "SHREDDED_GOBBLE",
  MOOO = "MOOO",
  OINK = "OINK",
  QUACK = "QUACK",
  SPLASH = "SPLASH",
  BAAA = "BAAA",
}

export type TFreshRecipe = keyof typeof FreshRecipe;

// This is mostly to remind us to update the fresh recipe enum when we add a new recipe.
export const IS_FRESH_RECIPE: Record<TFreshRecipe, true>
& Record<Exclude<SubscriptionItemType, TFreshRecipe>, false> = {
  [SubscriptionItemType.CLUCK]: true,
  [SubscriptionItemType.GOBBLE]: true,
  [SubscriptionItemType.SHREDDED_CLUCK]: true,
  [SubscriptionItemType.SHREDDED_GOBBLE]: true,
  [SubscriptionItemType.MOOO]: true,
  [SubscriptionItemType.OINK]: true,
  [SubscriptionItemType.QUACK]: true,
  [SubscriptionItemType.SPLASH]: true,
  [SubscriptionItemType.BAAA]: true,
  [SubscriptionItemType.SCOOP_PLANET]: false,
  [SubscriptionItemType.SCOOP_HEALTH]: false,
  [SubscriptionItemType.NIBBLES_SALMON]: false,
  [SubscriptionItemType.NIBBLES_CHICKEN]: false,
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: false,
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: false,
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: false,
  [SubscriptionItemType.SPRINKLES]: false,
  [SubscriptionItemType.TOTE_BAG]: false,
};

export function isFreshRecipe(type: string): type is TFreshRecipe {
  return !!(IS_FRESH_RECIPE as Partial<Record<string, boolean>>)[type];
}

export type TFreshRecipeQuantities = Record<TFreshRecipe, number | null | undefined>;

export interface IUpdateRecipesRequest {
  subscriptionId: number;
  recipes: Partial<TFreshRecipeQuantities>;
}

export interface IUpdateRecipesResponse {
  subscriptionId: number;
  recipes:TFreshRecipeQuantities;
}

export const ALLERGEN_TO_RECIPES_MAP: Record<Allergen, TFreshRecipe[]> = {
  [Allergen.CHICKEN]: [
    FreshRecipe.CLUCK,
    FreshRecipe.SHREDDED_CLUCK,
    FreshRecipe.GOBBLE,
    FreshRecipe.SHREDDED_GOBBLE,
    FreshRecipe.QUACK,
  ],
  [Allergen.TURKEY]: [
    FreshRecipe.GOBBLE,
    FreshRecipe.SHREDDED_GOBBLE,
    FreshRecipe.SPLASH,
  ],
  [Allergen.BEEF]: [
    FreshRecipe.MOOO,
    FreshRecipe.BAAA,
  ],
  [Allergen.PORK]: [
    FreshRecipe.OINK,
  ],
  [Allergen.LAMB]: [
    FreshRecipe.BAAA,
  ],
  [Allergen.WHITEFISH]: [
    FreshRecipe.SPLASH,
  ],
  [Allergen.DUCK]: [
    FreshRecipe.QUACK,
  ],
  [Allergen.SALMON]: [
    FreshRecipe.QUACK,
    FreshRecipe.OINK,
    FreshRecipe.MOOO,
    FreshRecipe.BAAA,
    FreshRecipe.SPLASH,
    FreshRecipe.SHREDDED_CLUCK,
    FreshRecipe.SHREDDED_GOBBLE,
  ],
  [Allergen.TUNA]: [],
};

export const containsAllergen = (
  recipe: TFreshRecipe,
  allergen: Allergen,
) => ALLERGEN_TO_RECIPES_MAP[allergen].includes(recipe);

export const FRESH_RECIPE_VARIANTS: ReadonlyArray<SubscriptionItemVariantValue> = [
  SubscriptionItemVariantValue.A,
  SubscriptionItemVariantValue.B,
  SubscriptionItemVariantValue.C,
  SubscriptionItemVariantValue.D,
  SubscriptionItemVariantValue.E,
  SubscriptionItemVariantValue.F,
];

export interface IGetFreshMealPlanPricingInfoMapConfig {
  includeTrialPlans?: boolean,
}
export const SHREDDED_FRESH_RECIPES: ReadonlyArray<TFreshRecipe> = [
  FreshRecipe.SHREDDED_CLUCK,
  FreshRecipe.SHREDDED_GOBBLE,
];

export const isShreddedFreshRecipe = (name: SubscriptionItemType): boolean =>
  (SHREDDED_FRESH_RECIPES as ReadonlyArray<SubscriptionItemType>).includes(name);

export const hasActiveShreddedItem = (
  freshMealPlans: ReadonlyArray<{ items: ReadonlyArray<{ name: SubscriptionItemType }> }>,
): boolean =>
  freshMealPlans.some((plan) => plan.items.some((item) => isShreddedFreshRecipe(item.name)));
